import { useEffect } from "react"

import { AppBar, Avatar, IconButton, Menu, Toolbar } from "@mui/material"

import {
    CreditCardOutlined as CreditCardOutlinedIcon,
    DashboardOutlined as DashboardOutlinedIcon,
    HelpOutline as HelpOutlineIcon,
    Logout as LogoutIcon,
    SettingsOutlined as SettingsOutlinedIcon,
    SupportAgent as SupportAgentIcon,
    WhatsApp as WhatsAppIcon,
} from "@mui/icons-material"

import { useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import kwikScoutLogo from "../assets/kwik-scout-logo.png"

import { clearAuthSession, getStoredUser } from "../services/auth"
import { getInitials } from "../services/scout"
import { getHomeDashboardConfig } from "../services/home-config"
import { getSettingsProfileMenuOverrides } from "../services/settings-config"
import { emitSnackbar } from "../lib/snackbar-emitter";

import { SUPPORT_EMAIL } from "../config"
import { getScoutEnterprise, getSupportUrl } from "../services/scout"
import { getSupportType, getManagerUrl } from "../services/utils"

const AppHeader = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [anchorEl, setAnchorEl] = useState(null)
    const homeConfig = useMemo(() => getHomeDashboardConfig(), [])
    const settingsMenuOverrides = useMemo(() => getSettingsProfileMenuOverrides(), [])
    const profileMenu = homeConfig?.profileMenu || {}
    const user = getStoredUser()
    const initials = getInitials(user?.first_name || user?.username)
    const isDashboard = location.pathname.startsWith("/home") || location.pathname.startsWith("/conversas")
    const isSettingsPage = location.pathname.startsWith("/configuracoes")
    const isMenuOpen = Boolean(anchorEl)
    
    const displayName = useMemo(() => {
        return user?.full_name || user?.name || user?.first_name || profileMenu?.displayNameFallback || "Usuário"
    }, [profileMenu?.displayNameFallback, user])
    const email = user?.email || profileMenu?.emailFallback || user?.username || ""

    const [enterprise, setEnterprise] = useState(null)
    const [supportAnchorEl, setSupportAnchorEl] = useState(null)
    const isSupportOpen = Boolean(supportAnchorEl)

    useEffect(() => {
        const load = async () => {
            setEnterprise(await getScoutEnterprise())
        }
        load()
    }, [])

    const handleLogout = () => {
        clearAuthSession()
        navigate("/login", { replace: true })
    }

    const handleMenuAction = (action) => {
        setAnchorEl(null)

        if (!action?.type) {
            return
        }

        if (action.type === "navigate" && action.path) {
            const paymentsPath = settingsMenuOverrides?.paymentsPath || settingsMenuOverrides?.billingPath

            if (action.path === "/planos" && paymentsPath) {
                navigate(paymentsPath)
                return
            }

            navigate(action.path)
            return
        }

        if (action.type === "logout") {
            handleLogout()
        }
    }

    const getMenuItemIcon = (id) => {
        if (id === "billing" || id === "payments") {
            return <CreditCardOutlinedIcon />
        }

        if (id === "settings") {
            return <SettingsOutlinedIcon />
        }

        if (id === "support") {
            return <HelpOutlineIcon />
        }

        return <LogoutIcon />
    }

    const handleHelpClick = async (event) => {
        const anchor = event.currentTarget

        const supportType = getSupportType(enterprise)

        switch (supportType) {
            case "email":
                openSupport("mailto")
                break
            case "priority":
                openSupport("builder")
                break
            case "manager":
                setSupportAnchorEl(anchor)
                break
            default:
                emitSnackbar({message: "Recurso indisponível no seu plano.", severity: "warning"})
                break
        }
    }

    const openSupport = async (type) => {
        switch (type) {
            case "mailto":
                window.location.href = `mailto:${SUPPORT_EMAIL}`
                break
            case "builder":
                const supportUrl = await getSupportUrl()
                window.open(supportUrl, "_blank", "noopener,noreferrer")
                break
            case "manager":
                try {
                    const managerUrl = getManagerUrl(enterprise)   
                    window.open(managerUrl, "_blank", "noopener,noreferrer")
                } catch (err) {
                    emitSnackbar({message: "Gerente de conta não configurado.", severity: "warning"})
                }
                break
            default:
                emitSnackbar({message: "Recurso indisponível no seu plano.", severity: "warning"})
                break
        }
    }

    return (
        <AppBar
            className="app-toolbar scout-appbar"
            elevation={0}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
            <Toolbar className="scout-appbar__toolbar">
                <button
                    type="button"
                    className="scout-appbar__brand"
                    onClick={() => navigate("/home")}
                >
                    <img src={kwikScoutLogo} alt="Kwik Scout" className="scout-appbar__logo" />
                </button>

                <div className="scout-appbar__actions">
                    <IconButton
                        className={isDashboard ? "scout-appbar__icon scout-appbar__icon--active" : "scout-appbar__icon"}
                        onClick={() => navigate("/home")}
                    >
                        <DashboardOutlinedIcon />
                    </IconButton>
                    <IconButton
                        className={isSettingsPage ? "scout-appbar__icon scout-appbar__icon--active" : "scout-appbar__icon"}
                        onClick={() => navigate("/configuracoes")}
                    >
                        <SettingsOutlinedIcon />
                    </IconButton>
                    <IconButton className="scout-appbar__icon" onClick={(event) => handleHelpClick(event)}>
                        <HelpOutlineIcon />
                    </IconButton>
                    <Menu
                        anchorEl={supportAnchorEl}
                        open={isSupportOpen}
                        onClose={() => setSupportAnchorEl(null)}
                        transformOrigin={{ horizontal: "right", vertical: "top" }}
                        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                        PaperProps={{ className: "scout-support-menu" }}
                    >
                        <div className="scout-support-menu__items">
                            <button type="button" className="scout-support-menu__item" onClick={() => openSupport("builder")}>
                                <SupportAgentIcon />
                                <span>Falar com atendente</span>
                            </button>
                            <button type="button" className="scout-support-menu__item" onClick={() => openSupport("manager")}>
                                <WhatsAppIcon />
                                <span>Falar com meu gerente</span>
                            </button>
                        </div>
                    </Menu>
                    <span className="scout-appbar__divider" />
                    <button type="button" className="scout-appbar__avatar-button" onClick={(event) => setAnchorEl(event.currentTarget)}>
                        <Avatar className="scout-appbar__avatar">{initials}</Avatar>
                    </button>
                    <Menu
                        anchorEl={anchorEl}
                        open={isMenuOpen}
                        onClose={() => setAnchorEl(null)}
                        transformOrigin={{ horizontal: "right", vertical: "top" }}
                        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                        PaperProps={{ className: "scout-profile-menu" }}
                        MenuListProps={{ sx: { py: 0 }}}
                    >
                        <div className="scout-profile-menu__header">
                            <Avatar className="scout-appbar__avatar scout-appbar__avatar--large">{initials}</Avatar>
                            <strong>{displayName}</strong>
                            <span>{email}</span>
                        </div>

                        <div className="scout-profile-menu__items">
                            {(profileMenu?.items || []).map((item) => (
                                <button key={item.id} type="button" className="scout-profile-menu__item" onClick={() => handleMenuAction(item.action)}>
                                    {getMenuItemIcon(item.id)}
                                    <span>{item.label}</span>
                                </button>
                            ))}
                        </div>

                        {/*<div className="scout-profile-menu__footer"></div>*/}
                    </Menu>
                </div>
            </Toolbar>
        </AppBar>
    )
}

export default AppHeader
